<script lang="ts">
  import { Quirofano, Episodios, type Sala, type Cirugia, type Episodio } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';

  let salas = $state<Sala[]>([]);
  let agenda = $state<Cirugia[]>([]);
  let episodios = $state<Episodio[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);

  // ventana de agenda: hoy → +7 días, en UTC ISO con 'Z'
  const _now = new Date();
  const _in7 = new Date(_now.getTime() + 7 * 86400_000);
  const _iso = (d: Date) => d.toISOString().replace(/\.\d+Z$/, 'Z');
  let desde = $state(_iso(_now));
  let hasta = $state(_iso(_in7));

  let nuevaSala = $state<Sala>({ codigo: '', nombre: '', estado: 'DISPONIBLE' });
  let nuevaCir = $state<Cirugia>({
    pacienteId: '', salaId: '', procedimiento: '',
    fechaProgramada: new Date(Date.now() + 3600_000).toISOString().slice(0, 16),
    duracionEstimadaMin: 60
  });

  async function cargar() {
    loading = true; error = null;
    try {
      [salas, agenda] = await Promise.all([Quirofano.salas(), Quirofano.agenda(desde, hasta)]);
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function crearSala(e: SubmitEvent) {
    e.preventDefault();
    try { await Quirofano.crearSala({ ...nuevaSala }); nuevaSala = { codigo: '', nombre: '', estado: 'DISPONIBLE' }; await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function programar(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevaCir.pacienteId || !nuevaCir.salaId) { error = 'Paciente y sala son obligatorios.'; return; }
    try {
      await Quirofano.programar({
        ...nuevaCir,
        episodioId: nuevaCir.episodioId || null,
        fechaProgramada: _iso(new Date(nuevaCir.fechaProgramada))
      });
      nuevaCir = { ...nuevaCir, pacienteId: '', procedimiento: '', episodioId: null };
      episodios = [];
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function cargarEpisodios(pid: string) {
    if (!pid) { episodios = []; return; }
    try {
      const todos = await Episodios.porPaciente(pid);
      episodios = todos.filter(e => e.estado === 'ABIERTO' || e.estado === 'EN_ATENCION');
    } catch { episodios = []; }
  }

  $effect(() => { cargar(); });
  $effect(() => { cargarEpisodios(nuevaCir.pacienteId); });
</script>

<section>
  <header><h2>Quirófano</h2><button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button></header>
  {#if error}<p class="err">⚠ {error}</p>{/if}

  <h3>Salas</h3>
  <form onsubmit={crearSala} class="row">
    <input placeholder="Código (Q-1)" bind:value={nuevaSala.codigo} required />
    <input placeholder="Nombre" bind:value={nuevaSala.nombre} />
    <select bind:value={nuevaSala.estado}>
      <option>DISPONIBLE</option><option>OCUPADA</option><option>LIMPIEZA</option><option>MANTENIMIENTO</option>
    </select>
    <button type="submit">+ Sala</button>
  </form>
  <table>
    <thead><tr><th>Código</th><th>Nombre</th><th>Estado</th></tr></thead>
    <tbody>
      {#each salas as s (s.id)}
        <tr><td>{s.codigo}</td><td>{s.nombre ?? '—'}</td><td><span class="badge {s.estado?.toLowerCase()}">{s.estado}</span></td></tr>
      {:else}<tr><td colspan="3" class="empty">No hay salas de quir�fano.</td></tr>{/each}
    </tbody>
  </table>

  <h3 style="margin-top:1.5rem">Programar cirugía</h3>
  <form onsubmit={programar} class="form-grid">
    <PacientePicker bind:value={nuevaCir.pacienteId} onPick={(id) => nuevaCir.pacienteId = id} />
    <select bind:value={nuevaCir.salaId} required>
      <option value="">— Sala —</option>
      {#each salas as s}<option value={s.id}>{s.codigo} {s.nombre ? '· ' + s.nombre : ''}</option>{/each}
    </select>
    <select bind:value={nuevaCir.episodioId} disabled={episodios.length === 0}>
      <option value="">— Episodio (opcional) —</option>
      {#each episodios as ep}<option value={ep.id}>{ep.numero} · {ep.tipo}</option>{/each}
    </select>
    <input placeholder="Procedimiento" bind:value={nuevaCir.procedimiento} required />
    <input placeholder="Cirujano (id/nombre)" bind:value={nuevaCir.cirujanoId} />
    <input type="datetime-local" bind:value={nuevaCir.fechaProgramada} required />
    <input type="number" min="15" step="15" placeholder="Duración (min)" bind:value={nuevaCir.duracionEstimadaMin} />
    <button type="submit" class="full">Programar</button>
  </form>

  <h3 style="margin-top:1.5rem">Agenda (próximos 7 días)</h3>
  <table>
    <thead><tr><th>Fecha</th><th>Procedimiento</th><th>Paciente</th><th>Sala</th><th>Estado</th></tr></thead>
    <tbody>
      {#each agenda as c (c.id)}
        <tr>
          <td>{new Date(c.fechaProgramada).toLocaleString()}</td>
          <td>{c.procedimiento}</td>
          <td><code>{c.pacienteId.slice(0,8)}…</code></td>
          <td>{salas.find(s => s.id === c.salaId)?.codigo ?? c.salaId.slice(0,8)+'…'}</td>
          <td><span class="badge">{c.estado}</span></td>
        </tr>
      {:else}<tr><td colspan="5" class="empty">Sin cirugías programadas.</td></tr>{/each}
    </tbody>
  </table>
</section>

<style>
  header { display: flex; justify-content: space-between; align-items: center; }
  h3 { margin: 1rem 0 .5rem; font-size: 1rem; color: #555; }
  .row { display: flex; gap: .5rem; flex-wrap: wrap; margin-bottom: .5rem; }
  .form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: .5rem; }
  .form-grid > :global(.picker), .form-grid > input, .form-grid > select, .form-grid > button { padding: .45rem .6rem; border-radius: 8px; border: 1px solid #ccc; font: inherit; }
  .full, button[type=submit] { background: #0d6efd; color: #fff; border: 0; cursor: pointer; }
  .full { grid-column: span 3; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: .45rem .6rem; border-bottom: 1px solid #eee; text-align: left; font-size: .9rem; }
  .badge { padding: .15rem .55rem; border-radius: 999px; font-size: .75rem; background: #eef; }
  .badge.disponible { background: #d6f5d6; color: #1a6b1a; }
  .badge.ocupada { background: #fde2e2; color: #a02020; }
  .badge.limpieza { background: #fff4cc; color: #8a6a00; }
  .empty { text-align: center; color: #888; font-style: italic; }
  .err { color: #b00020; }
  code { background: #f4f4f4; padding: .1rem .35rem; border-radius: 4px; font-size: .8rem; }
</style>
